import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Github, ExternalLink, Star, Lock, EyeOff, ArrowUp } from 'lucide-react';

const Projects = ({ projectsRef, projects }) => {
  const [popup, setPopup] = useState({
    show: false,
    message: '',
    icon: null,
    position: { x: 0, y: 0 },
    projectId: null
  });

  // Tracks which card is mid hover so we can drive a subtle tilt effect
  // purely with local state, no extra libraries needed.
  const [tilt, setTilt] = useState({ id: null, x: 0, y: 0 });

  const showPopup = (message, icon, projectId, position) => {
    setPopup({
      show: true,
      message,
      icon,
      position,
      projectId
    });

    setTimeout(() => {
      setPopup(prev => ({ ...prev, show: false }));
    }, 2500);
  };

  const handleLinkClick = (type, link, projectId, event) => {
    event.preventDefault();
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    const position = {
      x: rect.left + rect.width / 2,
      y: rect.top
    };

    if (!link || link === '#') {
      if (type === 'code') {
        showPopup(
          "Sorry, this codebase is currently private due to confidentiality.",
          <Lock size={20} />,
          projectId,
          position
        );
      } else {
        showPopup(
          "This project isn't publicly deployed yet. Get in touch for a demo",
          <EyeOff size={20} />,
          projectId,
          position
        );
      }
    } else {
      window.open(link, '_blank');
    }
  };

  // Gentle tilt toward the cursor. Kept small (max ~6deg) so it reads as
  // polished rather than gimmicky, and it resets smoothly on mouse leave.
  const handleCardMouseMove = (event, id) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width - 0.5;
    const py = (event.clientY - rect.top) / rect.height - 0.5;
    setTilt({ id, x: px * -6, y: py * 6 });
  };

  const handleCardMouseLeave = () => {
    setTilt({ id: null, x: 0, y: 0 });
  };

  return (
    <section id="projects" ref={projectsRef} className="projects">
      <div className="section-container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">
            <span className="title-number">02.</span>
            My Projects
            <motion.div
              className="title-underline"
              layoutId="projectsUnderline"
            />
          </h2>
          <p className="section-subtitle">
            Revolutionary solutions that redefine possibilities
          </p>
        </motion.div>

        <div className="projects-container">
          {projects.map((project, index) => {
            const isTilted = tilt.id === project.id;
            return (
              <motion.div
                key={project.id}
                className={`project-card ${project.featured ? 'featured-project' : ''}`}
                initial={{ opacity: 0, y: 60, scale: 0.96 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: false, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1, type: 'spring', bounce: 0.25, stiffness: 200, damping: 20 }}
                onMouseMove={(e) => handleCardMouseMove(e, project.id)}
                onMouseLeave={handleCardMouseLeave}
                animate={{
                  y: isTilted ? -10 : 0,
                  rotateX: isTilted ? tilt.y : 0,
                  rotateY: isTilted ? tilt.x : 0,
                }}
                style={{ transformPerspective: 900, transformStyle: 'preserve-3d' }}
              >
                <motion.div
                  className="project-number"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false }}
                  transition={{ delay: index * 0.1 + 0.2, duration: 0.5 }}
                >
                  {String(index + 1).padStart(2, '0')}
                </motion.div>

                <div className="project-image">
                  <motion.img
                    src={project.image}
                    alt={project.title}
                    className="project-img"
                    initial={{ scale: 1 }}
                    whileHover={{ scale: 1.08 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  />
                  <motion.div
                    className="project-overlay"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="project-links">
                      <motion.a
                        href={project.github || '#'}
                        className="project-link"
                        initial={{ opacity: 0, y: 16 }}
                        whileHover={{ y: -4, scale: 1.05 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: false }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ delay: 0.05, duration: 0.3 }}
                        onClick={(e) => handleLinkClick('code', project.github, project.id, e)}
                      >
                        <Github size={24} />
                        <span>Code</span>
                      </motion.a>
                      <motion.a
                        href={project.live || '#'}
                        className="project-link"
                        initial={{ opacity: 0, y: 16 }}
                        whileHover={{ y: -4, scale: 1.05 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: false }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ delay: 0.1, duration: 0.3 }}
                        onClick={(e) => handleLinkClick('live', project.live, project.id, e)}
                      >
                        <ExternalLink size={24} />
                        <span>Live</span>
                      </motion.a>
                    </div>
                  </motion.div>
                  {project.featured && (
                    <motion.div
                      className="featured-badge"
                      style={{ position: 'relative', overflow: 'hidden' }}
                      animate={{ scale: [1, 1.06, 1] }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <Star size={16} />
                      <span>Featured</span>
                      {/* Soft shimmer sweep instead of the old wobble, reads cleaner */}
                      <motion.span
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: '-60%',
                          width: '50%',
                          height: '100%',
                          background: 'linear-gradient(120deg, transparent, rgba(255,255,255,0.55), transparent)',
                        }}
                        animate={{ left: ['-60%', '140%'] }}
                        transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 1.4, ease: 'easeInOut' }}
                      />
                    </motion.div>
                  )}
                </div>

                <div className="project-content">
                  <motion.h3
                    className="project-title"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false }}
                    transition={{ delay: index * 0.1 + 0.25, duration: 0.5 }}
                  >
                    {project.title}
                  </motion.h3>
                  <motion.p
                    className="project-description"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: false }}
                    transition={{ delay: index * 0.1 + 0.35, duration: 0.5 }}
                  >
                    {project.description}
                  </motion.p>
                  <div className="project-tech">
                    {project.tech.map((tech, techIndex) => (
                      <motion.span
                        key={techIndex}
                        className="tech-tag"
                        initial={{ opacity: 0, y: 8, scale: 0.9 }}
                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                        viewport={{ once: false }}
                        whileHover={{ y: -3, scale: 1.06 }}
                        transition={{ delay: index * 0.1 + 0.4 + techIndex * 0.04, duration: 0.35 }}
                      >
                        {tech}
                      </motion.span>
                    ))}
                  </div>
                </div>

                <motion.div
                  className="project-glow"
                  animate={{ opacity: [0.3, 0.6, 0.3] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {popup.show && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.92 }}
              transition={{ type: 'spring', damping: 22, stiffness: 300 }}
              className="connected-popup"
              style={{
                left: `${popup.position.x}px`,
                top: `${popup.position.y - 100}px`,
              }}
            >
              <motion.div
                className="popup-arrow"
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ArrowUp size={16} />
              </motion.div>
              <div className="popup-content">
                <motion.div
                  className="popup-icon"
                  initial={{ rotate: -15, scale: 0.7 }}
                  animate={{ rotate: 0, scale: 1 }}
                  transition={{ type: 'spring', bounce: 0.6, duration: 0.5 }}
                >
                  {popup.icon}
                </motion.div>
                <div className="popup-message">
                  {popup.message}
                </div>
                <motion.div
                  className="popup-progress"
                  initial={{ scaleX: 1 }}
                  animate={{ scaleX: 0 }}
                  transition={{ duration: 2.5, ease: "linear" }}
                />
              </div>
            </motion.div>

            {/* Connection line */}
            <svg className="popup-connector">
              <line
                x1={popup.position.x}
                y1={popup.position.y - 20}
                x2={popup.position.x}
                y2={popup.position.y - 80}
                stroke="var(--accent-color)"
                strokeWidth="2"
                strokeDasharray="5,3"
              />
            </svg>
          </>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;